/**
 * Hint quality — the session list.
 *
 * One row per hint session (a student's single attempt at one question),
 * newest first. The rollup tab asks "which questions need work"; this asks
 * "what has been happening", and is the way in when you want to read a
 * specific student's session rather than a question's aggregate.
 *
 * Row click opens that exact session's detail.
 *
 * See docs/FeatureDesigns/HintSystem/hint-quality-review.md §7.
 */

import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '../api';
import { LatexText } from '../latex';
import {
  DataTable, SearchFilterBar, Pagination,
  type ColumnDef, type FilterDef, type SortState,
} from '../components';
import { useSubjects } from '../reference-data';
import { createHintQualityApi, sinceDateFromRange } from './hintQualityApi';
import { ReviewCell } from './ReviewCell';
import {
  HINT_LEVELS, HINT_CATEGORY_LABELS, HINT_REVIEW_FILTER_OPTIONS,
  type HintQualitySessionRow, type HintQualityAssignmentOption,
} from './types';

const PAGE_SIZE = 25;

const DATE_RANGES = [
  { value: '30', label: 'Last 30 days' },
  { value: '7', label: 'Last 7 days' },
  { value: '90', label: 'Last 90 days' },
  { value: '', label: 'All time' },
];

interface Filters {
  dateRange: string;
  subject: string;
  search: string;
  context: string;
  level: string;
  feedback: string;
  questionSetId: string;
  review: string;
}

/** Every filter starts empty except the date window — no assignment selected
 *  means no assignment filter, i.e. exactly the unfiltered view. */
const DEFAULT_FILTERS: Filters = {
  dateRange: '30', subject: '', search: '', context: '', level: '', feedback: '',
  questionSetId: '', review: '',
};

/** Start time, 24-hour. `hourCycle: 'h23'` rather than `hour12: false`, which
 *  renders midnight as 24:00 in some locales. */
function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString([], { day: 'numeric', month: 'short' }) +
    ', ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
}

/** Assignment options carry the date they were given — the title alone rarely
 *  says which one you mean. */
function formatAssignmentLabel(a: { title: string; date?: string }): string {
  if (!a.date) return a.title;
  const d = new Date(a.date);
  if (Number.isNaN(d.getTime())) return a.title;
  return `${a.title} (${d.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' })})`;
}

export interface HintQualitySessionsTabProps {
  /** Opens one session's detail. The host owns routing. */
  onSessionClick: (row: HintQualitySessionRow) => void;
  /** Pre-fills the search box — set when arriving from a student row. */
  initialSearch?: string;
}

const EMPTY_REASON_LABELS: Record<string, string> = {
  generating: 'still generating',
  failed: 'generation failed',
  abandoned: 'no hint shown',
};

export function HintQualitySessionsTab({ onSessionClick, initialSearch }: HintQualitySessionsTabProps) {
  const api = useApi();
  const hintQualityApi = useMemo(() => createHintQualityApi(api), [api]);
  const { subjects } = useSubjects();

  const [filters, setFilters] = useState<Filters>(
    initialSearch ? { ...DEFAULT_FILTERS, search: initialSearch } : DEFAULT_FILTERS);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortState>({ sortBy: 'startedAt', sortOrder: 'desc' });
  // Options are independent of the current filters, so keep the last non-empty
  // list rather than letting an empty result blank the dropdown.
  const [assignmentOptions, setAssignmentOptions] = useState<HintQualityAssignmentOption[]>([]);

  const filterDefs = useMemo<FilterDef[]>(() => [
    { key: 'dateRange', kind: 'select', placeholder: 'Date range', options: DATE_RANGES, required: true },
    {
      key: 'subject',
      kind: 'select',
      placeholder: 'All subjects',
      options: subjects.map(s => ({ value: s.name, label: s.displayName })),
    },
    // Narrows to the questions the chosen assignment contains, whichever
    // context the hint was taken in.
    {
      key: 'questionSetId',
      kind: 'select',
      placeholder: 'All assignments',
      options: assignmentOptions.map(a => ({ value: a.id, label: formatAssignmentLabel(a) })),
    },
    {
      key: 'context',
      kind: 'select',
      placeholder: 'Anywhere',
      options: [
        { value: 'assignment', label: 'In an assignment' },
        { value: 'practice', label: 'Free practice' },
      ],
    },
    {
      key: 'level',
      kind: 'select',
      placeholder: 'Any depth',
      options: HINT_LEVELS.map(l => ({ value: l.value, label: `Reached ${l.label}` })),
    },
    {
      key: 'review',
      kind: 'select',
      placeholder: 'Any review state',
      options: HINT_REVIEW_FILTER_OPTIONS,
    },
    {
      key: 'feedback',
      kind: 'select',
      placeholder: 'Any outcome',
      options: [
        { value: 'negative', label: 'Has 👎' },
        { value: 'positive', label: 'Has 👍' },
        { value: 'unrated', label: 'Unrated' },
        // The student asked and nothing arrived — a hint failure worth finding.
        { value: 'undelivered', label: 'No hint delivered' },
      ],
    },
  ], [subjects, assignmentOptions]);

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['hint-quality-sessions', page, filters, sort],
    queryFn: () => hintQualityApi.getSessions({
      page,
      pageSize: PAGE_SIZE,
      sinceDate: sinceDateFromRange(filters.dateRange),
      subject: filters.subject || undefined,
      search: filters.search || undefined,
      context: filters.context || undefined,
      level: filters.level || undefined,
      feedback: filters.feedback || undefined,
      questionSetId: filters.questionSetId || undefined,
      review: filters.review || undefined,
      sortBy: sort.sortBy,
      sortDir: sort.sortOrder,
    }),
  });

  const rows = data?.items || [];
  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0;

  useEffect(() => {
    if (data?.assignments?.length) setAssignmentOptions(data.assignments);
  }, [data?.assignments]);

  const columns = useMemo<ColumnDef<HintQualitySessionRow>[]>(() => [
    {
      key: 'startedAt',
      header: 'When',
      sortable: true,
      width: '135px',
      render: r => formatWhen(r.startedAt),
    },
    {
      key: 'student',
      header: 'Student',
      sortable: true,
      render: r => (
        <div className="hintq-stack">
          <strong>{r.student.name}</strong>
          {r.student.email ? <span className="hintq-sub">{r.student.email}</span> : null}
        </div>
      ),
    },
    {
      key: 'question',
      header: 'Question',
      sortable: true,
      render: r => (
        <div className="hintq-qcell">
          <LatexText text={r.questionText || '(no text)'} truncate={110} />
          {r.topic && <span className="hintq-qmeta">{r.topic}</span>}
        </div>
      ),
    },
    {
      key: 'hintsShown',
      header: 'Hints',
      sortable: true,
      className: 'hintq-num',
      render: r => (r.hintsShown === 0
        ? (
          // Not a bare zero: the student did ask, so say what became of it.
          <span className="hintq-badge hintq-badge--muted" title="The student asked for a hint but none was shown">
            {EMPTY_REASON_LABELS[r.emptyReason || 'abandoned']}
          </span>
        )
        : (
          <div className="hintq-stack">
            <strong>{r.hintsShown}</strong>
            {r.hintsPrewritten > 0 && (
              <span className="hintq-sub">{r.hintsLlm} AI · {r.hintsPrewritten} ready</span>
            )}
          </div>
        )),
    },
    {
      key: 'maxLevel',
      header: 'Depth',
      sortable: true,
      render: r => r.maxLevel
        ? <span className={`hintq-badge hintq-level--${r.maxLevel}`}>{r.maxLevel}</span>
        : <span className="hintq-muted" title="No AI hint — ready-made hints only">—</span>,
    },
    {
      key: 'categories',
      header: 'Asked for',
      render: r => (r.categories.length === 0
        ? <span className="hintq-muted">—</span>
        : (
          <div className="hintq-chips">
            {r.categories.slice(0, 2).map(c => (
              <span key={c} className="hintq-chip">{HINT_CATEGORY_LABELS[c] || c}</span>
            ))}
            {r.categories.length > 2 && (
              <span className="hintq-chip" title={r.categories.join(', ')}>+{r.categories.length - 2}</span>
            )}
          </div>
        )),
    },
    {
      key: 'context',
      header: 'Context',
      render: r => (
        <span className="hintq-sub">
          {r.inAssignment ? 'Assignment' : 'Practice'}{r.inputMode ? ` · ${r.inputMode}` : ''}
        </span>
      ),
    },
    {
      key: 'reviewedPoor',
      header: 'Review',
      sortable: true,
      render: r => <ReviewCell row={r} />,
    },
    {
      key: 'thumbsDown',
      header: 'Student',
      sortable: true,
      className: 'hintq-num',
      render: r => (r.thumbsUp + r.thumbsDown === 0
        ? <span className="hintq-muted">—</span>
        : (
          <div className="hintq-ratings">
            {r.thumbsUp > 0 && <span className="hintq-rating hintq-rating--up">👍 {r.thumbsUp}</span>}
            {r.thumbsDown > 0 && <span className="hintq-rating hintq-rating--down">👎 {r.thumbsDown}</span>}
          </div>
        )),
    },
  ], []);

  if (error) {
    return <div className="hintq-error">Couldn't load hint sessions: {(error as Error).message}</div>;
  }

  const filtered = filters.search || filters.subject || filters.context
    || filters.level || filters.feedback || filters.questionSetId || filters.review;

  return (
    <>
      <SearchFilterBar
        filters={filterDefs}
        values={filters as unknown as Record<string, string>}
        onChange={v => { setFilters(f => ({ ...f, ...(v as Partial<Filters>) })); setPage(1); }}
        searchPlaceholder="Search question, topic, student name or email…"
        loading={isFetching}
        hideFetch
        trailingActions={totalPages > 1 ? (
          <Pagination
            page={page}
            totalPages={totalPages}
            total={data?.total}
            onPageChange={setPage}
            disabled={isFetching}
          />
        ) : undefined}
      />

      <DataTable
        columns={columns}
        data={rows}
        rowKey={r => r.id}
        onRowClick={onSessionClick}
        sort={sort}
        onSortChange={s => { setSort(s); setPage(1); }}
        loading={isLoading}
        emptyMessage={filtered
          ? 'No hint sessions match these filters.'
          : 'No hints have been taken yet in this period.'}
      />
    </>
  );
}
